import { useEffect, useState } from 'react';
import { getSeoOverride } from '../services/api';
import { getTourCurrency } from '@/lib/utils';

const overrideCache = {};
const useSeoOverride = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const [override, setOverride] = useState(overrideCache[path] || null);
  useEffect(() => {
    if (overrideCache[path]) { setOverride(overrideCache[path]); return; }
    getSeoOverride(path).then(res => { overrideCache[path] = res.data || {}; setOverride(overrideCache[path]); }).catch(() => {});
  }, [path]);
  return override || {};
};

const currentUrl = () => `https://istanbulturda.com${typeof window !== 'undefined' ? window.location.pathname : '/'}`;

const defaultMeta = {
  siteName: 'İstanbul Turda',
  siteUrl: 'https://istanbulturda.com',
  defaultImage: 'https://istanbulturda.com/og-cover.jpg',
  defaultDescription: 'Profesyonel rehber eşliğinde İstanbul yürüyüş turları. Tarihi yarımada, Balat, Galata ve daha fazlası.',
  locale: 'tr_TR'
};

// Helper function to update meta tags
const updateMetaTag = (property, content, isOG = false) => {
  const selector = isOG ? `meta[property="${property}"]` : `meta[name="${property}"]`;
  let meta = document.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    if (isOG) {
      meta.setAttribute('property', property);
    } else {
      meta.setAttribute('name', property);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

export default function SEO({ 
  title = '', 
  description = '',
  image = '',
  url = '',
  type = 'website',
  noindex = false,
  fullTitle = ''
}) {
  const override = useSeoOverride();
  const safeTitle = String(title || '').trim();
  const safeDescription = String(override.description || description || defaultMeta.defaultDescription).trim();
  const safeImage = String(image || defaultMeta.defaultImage).trim();
  const safeUrl = String(url || currentUrl()).trim();
  const robots = noindex || override.noindex ? 'noindex, nofollow' : 'index, follow';
  
  const pageTitle = override.title
    ? override.title
    : fullTitle
      ? fullTitle
      : safeTitle 
      ? `${safeTitle} | ${defaultMeta.siteName}` 
      : defaultMeta.siteName;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update meta tags
    updateMetaTag('description', safeDescription);
    updateMetaTag('robots', robots);
    updateMetaTag('language', 'Turkish');
    updateMetaTag('author', 'VIATERRA TRAVEL AGENCY');
    
    // Update Open Graph tags
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', safeUrl, true);
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', safeDescription, true);
    updateMetaTag('og:image', safeImage, true);
    updateMetaTag('og:site_name', defaultMeta.siteName, true);
    updateMetaTag('og:locale', defaultMeta.locale, true);
    
    // Update Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', safeUrl);
    updateMetaTag('twitter:title', pageTitle);
    updateMetaTag('twitter:description', safeDescription);
    updateMetaTag('twitter:image', safeImage);
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', safeUrl);
  }, [pageTitle, safeDescription, safeImage, safeUrl, type, robots]);

  return null;
}

export function TourSEO({ tour }) {
  const safeTitle = tour?.title ? String(tour.title).trim() : '';
  const safeDescription = tour?.short_description 
    ? String(tour.short_description).substring(0, 160).trim() 
    : (tour?.description ? String(tour.description).substring(0, 160).trim() : '');
  const override = useSeoOverride();
  const safeImage = tour?.image_url ? String(tour.image_url).trim() : defaultMeta.defaultImage;
  const currency = getTourCurrency(tour);
  const pageTitle = override.title || (safeTitle ? `${safeTitle} | İstanbul Turda` : 'İstanbul Turda');
  const metaDescription = override.description || safeDescription;
  const tourUrl = currentUrl();

  useEffect(() => {
    if (!tour?.title) return;
    
    document.title = pageTitle;
    
    updateMetaTag('description', metaDescription);
    updateMetaTag('robots', override.noindex ? 'noindex, nofollow' : 'index, follow');
    updateMetaTag('og:url', tourUrl, true);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', tourUrl);
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', metaDescription, true);
    updateMetaTag('og:image', safeImage, true);
    updateMetaTag('og:type', 'product', true);
    updateMetaTag('product:price:amount', String(tour?.price || 0));
    updateMetaTag('product:price:currency', currency);
    
    // Add JSON-LD schema
    let script = document.querySelector('script[data-schema="tour"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-schema', 'tour');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      "name": safeTitle,
      "description": safeDescription,
      "image": safeImage,
      "url": tourUrl,
      ...(tour?.price ? {
        "offers": {
          "@type": "Offer",
          "price": tour.price,
          "priceCurrency": currency,
          "url": tourUrl,
          ...(tour?.next_departure ? { "availability": "https://schema.org/InStock", "validFrom": tour.next_departure } : {})
        }
      } : {}),
      "provider": {
        "@type": "TravelAgency",
        "name": "İstanbul Turda - VIATERRA TRAVEL AGENCY"
      }
    });
    return () => { const el = document.querySelector('script[data-schema="tour"]'); if (el) el.remove(); };
  }, [tour, pageTitle, safeDescription, metaDescription, safeImage, currency, safeTitle, tourUrl, override.noindex]);

  return null;
}

export function BlogSEO({ post }) {
  const safeTitle = post?.title ? String(post.title).trim() : '';
  const safeDescription = post?.excerpt 
    ? String(post.excerpt).substring(0, 160).trim() 
    : (post?.content ? String(post.content).substring(0, 160).trim() : '');
  const safeImage = post?.image_url ? String(post.image_url).trim() : defaultMeta.defaultImage;
  const pageTitle = safeTitle ? `${safeTitle} | İstanbul Turda Blog` : 'İstanbul Turda Blog';

  useEffect(() => {
    if (!post?.title) return;
    
    document.title = pageTitle;
    updateMetaTag('robots', 'index, follow');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl());
    
    updateMetaTag('description', safeDescription);
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', safeDescription, true);
    updateMetaTag('og:image', safeImage, true);
    updateMetaTag('og:type', 'article', true);
    updateMetaTag('article:author', post?.author || 'İstanbul Turda');
    updateMetaTag('article:section', post?.category || 'Blog');
    
    // Add JSON-LD schema
    let script = document.querySelector('script[data-schema="article"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-schema', 'article');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": safeTitle,
      "description": safeDescription,
      "image": safeImage,
      "author": {
        "@type": "Person",
        "name": post?.author || "İstanbul Turda"
      },
      "publisher": {
        "@type": "Organization",
        "name": "İstanbul Turda",
        "logo": {
          "@type": "ImageObject",
          "url": defaultMeta.defaultImage
        }
      },
      "datePublished": post?.created_at || new Date().toISOString()
    });
  }, [post, pageTitle, safeDescription, safeImage, safeTitle]);

  return null;
}
